/**
 * TraditionalCard.js - Authentic Traditional Playing Card Engine for Crazy Cribbage
 * Provides authentic traditional playing card layouts, pip matrices, and high-DPI renderers:
 * - Spot cards (2 through 10) feature precisely the matching count of suit glyphs (e.g. 2 has 2 suits,
 *   3 has 3 suits, 4 has 4 suits, etc.) in traditional inverted configurations.
 * - Ace features an iconic large centerpiece suit emblem.
 * - Court cards (Jack, Queen, King) feature royal heraldic frames, crowns, titles, and center crests.
 * - Wild Jokers feature star filigree and joker insignia.
 * - Dual corner indices (top-left and rotated 180° bottom-right).
 * Used for flat board table cards, Hand of Glory social share cards, game replays, and social previews.
 */

import { RANK_NAME, SUIT_SYM } from '../scoring/ScoringConstants.js';

export class TraditionalCard {
  /**
   * Normalizes any card representation into standard rank, suit, symbol, color, and flags.
   * @param {Object} c
   * @returns {{ rank: number, rankName: string, suit: string, suitSymbol: string, isRed: boolean, isJoker: boolean }}
   */
  static normalize(c) {
    if (!c) {
      return { rank: 1, rankName: 'A', suit: 'S', suitSymbol: '♠', isRed: false, isJoker: false };
    }

    const isJoker = Boolean(c.joker || c.isWild || c.wild || c.r === 0 || c.rank === 0) && !c.playAs;
    if (isJoker) {
      return { rank: 0, rankName: '★', suit: 'J', suitSymbol: '★', isRed: false, isJoker: true };
    }

    let r = c.playAs ? c.playAs.r : (c.rank !== undefined ? c.rank : (c.r !== undefined ? c.r : 1));
    let rankNum = typeof r === 'number' ? r : parseInt(r, 10);
    let rankName = String(c.rankName || RANK_NAME[r] || r || 'A').toUpperCase();

    if (rankName === '1' || rankNum === 1) { rankName = 'A'; rankNum = 1; }
    else if (rankName === '11' || rankNum === 11) { rankName = 'J'; rankNum = 11; }
    else if (rankName === '12' || rankNum === 12) { rankName = 'Q'; rankNum = 12; }
    else if (rankName === '13' || rankNum === 13) { rankName = 'K'; rankNum = 13; }
    else if (isNaN(rankNum)) {
      if (rankName === 'A') rankNum = 1;
      else if (rankName === 'J') rankNum = 11;
      else if (rankName === 'Q') rankNum = 12;
      else if (rankName === 'K') rankNum = 13;
      else rankNum = parseInt(rankName, 10) || 5;
    }

    let s = String(c.playAs ? c.playAs.s : (c.suit || c.s || 'S')).toUpperCase();
    let suit = 'S';
    let suitSymbol = '♠';

    if (s === 'H' || s.startsWith('HEART') || s === '♥') {
      suit = 'H';
      suitSymbol = '♥';
    } else if (s === 'D' || s.startsWith('DIAMOND') || s === '♦') {
      suit = 'D';
      suitSymbol = '♦';
    } else if (s === 'C' || s.startsWith('CLUB') || s === '♣') {
      suit = 'C';
      suitSymbol = '♣';
    } else if (s === 'S' || s.startsWith('SPADE') || s === '♠') {
      suit = 'S';
      suitSymbol = '♠';
    } else if (SUIT_SYM[s]) {
      suit = s;
      suitSymbol = SUIT_SYM[s];
    }

    const isRed = (cardOrSuitIsRed(c, suit));
    return { rank: rankNum, rankName, suit, suitSymbol, isRed, isJoker: false };
  }

  /**
   * Returns traditional grid layout matrix coordinates for spot cards (2 through 10).
   * Maps onto a 3-column, 5-row CSS grid.
   * @param {string} rankName
   * @returns {Array<{ col: number, row: number, inv: boolean }>}
   */
  static getPipLayout(rankName) {
    switch (rankName) {
      case '2':
        return [
          { col: 2, row: 1, inv: false },
          { col: 2, row: 5, inv: true }
        ];
      case '3':
        return [
          { col: 2, row: 1, inv: false },
          { col: 2, row: 3, inv: false },
          { col: 2, row: 5, inv: true }
        ];
      case '4':
        return [
          { col: 1, row: 1, inv: false },
          { col: 3, row: 1, inv: false },
          { col: 1, row: 5, inv: true },
          { col: 3, row: 5, inv: true }
        ];
      case '5':
        return [
          { col: 1, row: 1, inv: false },
          { col: 3, row: 1, inv: false },
          { col: 2, row: 3, inv: false },
          { col: 1, row: 5, inv: true },
          { col: 3, row: 5, inv: true }
        ];
      case '6':
        return [
          { col: 1, row: 1, inv: false },
          { col: 3, row: 1, inv: false },
          { col: 1, row: 3, inv: false },
          { col: 3, row: 3, inv: false },
          { col: 1, row: 5, inv: true },
          { col: 3, row: 5, inv: true }
        ];
      case '7':
        return [
          { col: 1, row: 1, inv: false },
          { col: 3, row: 1, inv: false },
          { col: 2, row: 2, inv: false },
          { col: 1, row: 3, inv: false },
          { col: 3, row: 3, inv: false },
          { col: 1, row: 5, inv: true },
          { col: 3, row: 5, inv: true }
        ];
      case '8':
        return [
          { col: 1, row: 1, inv: false },
          { col: 3, row: 1, inv: false },
          { col: 2, row: 2, inv: false },
          { col: 1, row: 3, inv: false },
          { col: 3, row: 3, inv: false },
          { col: 2, row: 4, inv: true },
          { col: 1, row: 5, inv: true },
          { col: 3, row: 5, inv: true }
        ];
      case '9':
        return [
          { col: 1, row: 1, inv: false },
          { col: 3, row: 1, inv: false },
          { col: 1, row: 2, inv: false },
          { col: 3, row: 2, inv: false },
          { col: 2, row: 3, inv: false },
          { col: 1, row: 4, inv: true },
          { col: 3, row: 4, inv: true },
          { col: 1, row: 5, inv: true },
          { col: 3, row: 5, inv: true }
        ];
      case '10':
        return [
          { col: 1, row: 1, inv: false },
          { col: 3, row: 1, inv: false },
          { col: 2, row: 2, inv: false },
          { col: 1, row: 2, inv: false },
          { col: 3, row: 2, inv: false },
          { col: 1, row: 4, inv: true },
          { col: 3, row: 4, inv: true },
          { col: 2, row: 4, inv: true },
          { col: 1, row: 5, inv: true },
          { col: 3, row: 5, inv: true }
        ];
      default:
        return [
          { col: 2, row: 3, inv: false }
        ];
    }
  }

  /**
   * Returns normalized canvas position offsets [xRatio, yRatio, isInverted]
   * for drawing pips on a 2D HTML5 canvas.
   * @param {string} rankName
   * @returns {Array<[number, number, boolean]>}
   */
  static getPipCanvasOffsets(rankName) {
    switch (rankName) {
      case '2':
        return [
          [0.50, 0.16, false],
          [0.50, 0.84, true]
        ];
      case '3':
        return [
          [0.50, 0.16, false],
          [0.50, 0.50, false],
          [0.50, 0.84, true]
        ];
      case '4':
        return [
          [0.26, 0.16, false], [0.74, 0.16, false],
          [0.26, 0.84, true],  [0.74, 0.84, true]
        ];
      case '5':
        return [
          [0.26, 0.16, false], [0.74, 0.16, false],
          [0.50, 0.50, false],
          [0.26, 0.84, true],  [0.74, 0.84, true]
        ];
      case '6':
        return [
          [0.26, 0.16, false], [0.74, 0.16, false],
          [0.26, 0.50, false], [0.74, 0.50, false],
          [0.26, 0.84, true],  [0.74, 0.84, true]
        ];
      case '7':
        return [
          [0.26, 0.16, false], [0.74, 0.16, false],
          [0.50, 0.33, false],
          [0.26, 0.50, false], [0.74, 0.50, false],
          [0.26, 0.84, true],  [0.74, 0.84, true]
        ];
      case '8':
        return [
          [0.26, 0.16, false], [0.74, 0.16, false],
          [0.50, 0.33, false],
          [0.26, 0.50, false], [0.74, 0.50, false],
          [0.50, 0.67, true],
          [0.26, 0.84, true],  [0.74, 0.84, true]
        ];
      case '9':
        return [
          [0.26, 0.16, false], [0.74, 0.16, false],
          [0.26, 0.38, false], [0.74, 0.38, false],
          [0.50, 0.50, false],
          [0.26, 0.62, true],  [0.74, 0.62, true],
          [0.26, 0.84, true],  [0.74, 0.84, true]
        ];
      case '10':
        return [
          [0.26, 0.16, false], [0.74, 0.16, false],
          [0.50, 0.28, false],
          [0.26, 0.38, false], [0.74, 0.38, false],
          [0.26, 0.62, true],  [0.74, 0.62, true],
          [0.50, 0.72, true],
          [0.26, 0.84, true],  [0.74, 0.84, true]
        ];
      default:
        return [
          [0.50, 0.50, false]
        ];
    }
  }

  /**
   * Renders the authentic traditional playing card face HTML markup.
   * Generates corner indices, central pip grid for 2..10, Ace emblem, or Court card crest.
   * @param {Object} c - Card object or instance
   * @param {Object} [opts={}]
   * @returns {string} HTML markup string
   */
  static renderHtml(c, opts = {}) {
    const card = TraditionalCard.normalize(c);
    const color = card.isJoker ? '#9333ea' : (card.isRed ? '#dc2626' : '#111827');
    const isCut = Boolean(opts.isCut);

    if (card.isJoker) {
      return `
        <div class="trad-card trad-joker" style="color:${color};">
          ${isCut ? '<span class="trad-cut-badge">CUT</span>' : ''}
          <div class="trad-corner top-left">
            <span class="trad-rank">★</span>
            <span class="trad-suit">★</span>
          </div>
          <div class="trad-center-art">
            <div class="trad-art-joker">
              <div class="trad-joker-icon">🃏</div>
              <div class="trad-joker-title">JOKER</div>
            </div>
          </div>
          <div class="trad-corner bottom-right">
            <span class="trad-rank">★</span>
            <span class="trad-suit">★</span>
          </div>
        </div>
      `;
    }

    const { rankName, suitSymbol } = card;

    let centerArt = '';
    if (rankName === 'A') {
      centerArt = `
        <div class="trad-art-ace">
          <span class="trad-ace-symbol">${suitSymbol}</span>
        </div>
      `;
    } else if (['K', 'Q', 'J'].includes(rankName)) {
      const courtTitle = rankName === 'K' ? 'KING' : (rankName === 'Q' ? 'QUEEN' : 'JACK');
      const courtCrown = rankName === 'K' ? '👑' : (rankName === 'Q' ? '♕' : '🛡️');
      centerArt = `
        <div class="trad-art-court court-${rankName.toLowerCase()}">
          <div class="trad-court-crown">${courtCrown}</div>
          <div class="trad-court-title">${courtTitle}</div>
          <div class="trad-court-suit">${suitSymbol}</div>
        </div>
      `;
    } else {
      // Spot pip cards (2 through 10)
      const pips = TraditionalCard.getPipLayout(rankName);
      const pipsHtml = pips.map(p => {
        const invClass = p.inv ? ' is-inverted' : '';
        return `<span class="trad-pip col-${p.col} row-${p.row}${invClass}" style="grid-column:${p.col}; grid-row:${p.row};">${suitSymbol}</span>`;
      }).join('');

      centerArt = `
        <div class="trad-art-pips pips-${rankName}">
          ${pipsHtml}
        </div>
      `;
    }

    return `
      <div class="trad-card trad-${card.isRed ? 'red' : 'black'}" style="color:${color};">
        ${isCut ? '<span class="trad-cut-badge">CUT</span>' : ''}
        <div class="trad-corner top-left">
          <span class="trad-rank">${rankName}</span>
          <span class="trad-suit">${suitSymbol}</span>
        </div>
        <div class="trad-center-art">
          ${centerArt}
        </div>
        <div class="trad-corner bottom-right">
          <span class="trad-rank">${rankName}</span>
          <span class="trad-suit">${suitSymbol}</span>
        </div>
      </div>
    `;
  }

  /**
   * Draws an authentic traditional playing card onto a 2D Canvas context.
   * Used for social image generation, downloads, and clipboard copies.
   * @param {CanvasRenderingContext2D} ctx
   * @param {Object} c
   * @param {number} x
   * @param {number} y
   * @param {number} w
   * @param {number} h
   * @param {Object} [opts={}]
   */
  static drawToCanvas(ctx, c, x, y, w, h, opts = {}) {
    if (!ctx) return;
    const card = TraditionalCard.normalize(c);
    const isCut = Boolean(opts.isCut || opts.isStarter);
    const color = card.isJoker ? '#9333ea' : (card.isRed ? '#dc2626' : '#000000');

    ctx.save();

    // Zero drop shadow on card numbers, ranks, and suit glyphs
    ctx.shadowBlur = 0;
    ctx.shadowColor = 'transparent';
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;

    // 1. Cardstock background with rounded corners
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    if (typeof ctx.roundRect === 'function') {
      ctx.roundRect(x, y, w, h, 8);
    } else {
      ctx.rect(x, y, w, h);
    }
    ctx.fill();

    // Perimeter border
    ctx.strokeStyle = isCut ? '#f59e0b' : 'rgba(0, 0, 0, 0.22)';
    ctx.lineWidth = isCut ? 3.5 : 1.5;
    ctx.stroke();

    // Starter Cut Badge
    if (isCut) {
      ctx.fillStyle = '#f59e0b';
      ctx.beginPath();
      const badgeW = Math.min(48, w - 24);
      if (typeof ctx.roundRect === 'function') {
        ctx.roundRect(x + (w - badgeW) / 2, y - 9, badgeW, 18, 9);
      } else {
        ctx.rect(x + (w - badgeW) / 2, y - 9, badgeW, 18);
      }
      ctx.fill();
      ctx.fillStyle = '#1e1b18';
      ctx.font = '900 10px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('CUT', x + w / 2, y);
    }

    // 2. Corner Indices
    const cornerPadX = 8;
    const cornerTopY = 20;
    const cornerRankFont = '800 18px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    const cornerSuitFont = '16px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

    // Top-left
    ctx.fillStyle = color;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    ctx.font = cornerRankFont;
    ctx.fillText(card.rankName, x + cornerPadX, y + cornerTopY);
    ctx.font = cornerSuitFont;
    ctx.fillText(card.suitSymbol, x + cornerPadX, y + cornerTopY + 16);

    // Bottom-right (180° inverted if context supports transforms)
    if (typeof ctx.translate === 'function' && typeof ctx.rotate === 'function') {
      ctx.save();
      ctx.translate(x + w - cornerPadX, y + h - 8);
      ctx.rotate(Math.PI);
      ctx.fillStyle = color;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'alphabetic';
      ctx.font = cornerRankFont;
      ctx.fillText(card.rankName, 0, 16);
      ctx.font = cornerSuitFont;
      ctx.fillText(card.suitSymbol, 0, 0);
      ctx.restore();
    } else {
      ctx.fillStyle = color;
      ctx.textAlign = 'right';
      ctx.textBaseline = 'alphabetic';
      ctx.font = cornerRankFont;
      ctx.fillText(card.rankName, x + w - cornerPadX, y + h - 24);
      ctx.font = cornerSuitFont;
      ctx.fillText(card.suitSymbol, x + w - cornerPadX, y + h - 8);
    }

    // 3. Center Artwork
    const cx = x + w / 2;
    const cy = y + h / 2;

    if (card.isJoker) {
      ctx.fillStyle = color;
      ctx.font = '40px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('🃏', cx, cy - 4);
      ctx.font = 'bold 12px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.fillText('JOKER', cx, cy + 28);
    } else if (card.rankName === 'A') {
      // Ace: 1 large centerpiece suit
      ctx.fillStyle = color;
      ctx.font = `${Math.round(h * 0.42)}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(card.suitSymbol, cx, cy + 4);
    } else if (['K', 'Q', 'J'].includes(card.rankName)) {
      // Court cards (Jack, Queen, King): Royal framed crest
      const courtW = w - 30;
      const courtH = h - 48;
      const courtX = x + 15;
      const courtY = y + 24;

      ctx.save();
      ctx.strokeStyle = '#d4af37';
      ctx.lineWidth = 1.8;
      ctx.strokeRect(courtX, courtY, courtW, courtH);

      ctx.fillStyle = 'rgba(254, 249, 235, 0.45)';
      ctx.fillRect(courtX + 1, courtY + 1, courtW - 2, courtH - 2);

      const title = card.rankName === 'K' ? 'KING' : (card.rankName === 'Q' ? 'QUEEN' : 'JACK');
      const crown = card.rankName === 'K' ? '👑' : (card.rankName === 'Q' ? '♕' : '🛡️');

      ctx.fillStyle = '#b45309';
      ctx.font = '16px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(crown, cx, courtY + 16);

      ctx.fillStyle = color;
      ctx.font = `${Math.round(h * 0.24)}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
      ctx.fillText(card.suitSymbol, cx, cy + 2);

      ctx.fillStyle = '#78350f';
      ctx.font = 'bold 10px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.fillText(title, cx, courtY + courtH - 12);
      ctx.restore();
    } else {
      // Spot cards (2 through 10)
      const innerX = x + 16;
      const innerY = y + 16;
      const innerW = w - 32;
      const innerH = h - 32;

      const pipOffsets = TraditionalCard.getPipCanvasOffsets(card.rankName);
      const pipFontSize = Math.round(w * 0.22);
      ctx.font = `${pipFontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
      ctx.fillStyle = color;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      pipOffsets.forEach(([fx, fy, inv]) => {
        const px = innerX + innerW * fx;
        const py = innerY + innerH * fy;
        if (inv && typeof ctx.translate === 'function' && typeof ctx.rotate === 'function') {
          ctx.save();
          ctx.translate(px, py);
          ctx.rotate(Math.PI);
          ctx.fillText(card.suitSymbol, 0, 0);
          ctx.restore();
        } else {
          ctx.fillText(card.suitSymbol, px, py);
        }
      });
    }

    ctx.restore();
  }
}

function cardOrSuitIsRed(c, suit) {
  if (c && typeof c.isRed === 'boolean') return c.isRed;
  return suit === 'H' || suit === 'D';
}
